import { useState } from 'react';
import { FileText, Calendar, User, Pencil, Check, X, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { CaseStatusBadge } from './CaseStatusBadge';
import type { Case } from '../../types';
import { formatDate } from '../../utils/formatters';

interface CaseCardProps {
  caseData: Case;
  onClick: (caseId: string) => void;
  onRename?: (caseId: string, newName: string) => void;
  onDelete?: (caseId: string) => void;
}

export function CaseCard({ caseData, onClick, onRename, onDelete }: CaseCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(caseData.name);

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(caseData.name);
    setIsEditing(true);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditName(caseData.name);
    setIsEditing(false);
  };

  const handleSave = (e: React.MouseEvent) => {
    e.stopPropagation();
    const trimmed = editName.trim();
    if (trimmed && trimmed !== caseData.name) {
      onRename?.(caseData.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      const trimmed = editName.trim();
      if (trimmed && trimmed !== caseData.name) {
        onRename?.(caseData.id, trimmed);
      }
      setIsEditing(false);
    } else if (e.key === 'Escape') {
      setEditName(caseData.name);
      setIsEditing(false);
    }
  };

  const truncateNotes = (notes: string | undefined, maxLength: number = 100) => {
    if (!notes) return null;
    if (notes.length <= maxLength) return notes;
    return notes.substring(0, maxLength) + '...';
  };

  return (
    <Card
      className="group cursor-pointer hover:shadow-md transition-shadow duration-200 hover:border-primary/50 overflow-hidden"
      onClick={() => onClick(caseData.id)}
    >
      <CardHeader className="pb-3 overflow-hidden">
        <div className="flex items-start justify-between gap-2 min-w-0">
          {isEditing ? (
            <div className="flex items-center gap-1.5 min-w-0 flex-1" onClick={(e) => e.stopPropagation()}>
              <input
                autoFocus
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onKeyDown={handleKeyDown}
                maxLength={500}
                className="text-base font-semibold border border-gray-300 rounded px-2 py-0.5 outline-none focus:border-blue-500 min-w-0 flex-1"
              />
              <button
                onClick={handleSave}
                className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0"
                aria-label="Save name"
              >
                <Check className="h-4 w-4 text-green-600" />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0"
                aria-label="Cancel editing"
              >
                <X className="h-4 w-4 text-gray-500" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 min-w-0 flex-1 overflow-hidden">
              <CardTitle className="text-base font-semibold truncate" title={caseData.name}>
                {caseData.name}
              </CardTitle>
              {onRename && (
                <button
                  onClick={handleStartEdit}
                  className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                  aria-label="Rename case"
                >
                  <Pencil className="h-3.5 w-3.5 text-gray-400" />
                </button>
              )}
              {onDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); onDelete(caseData.id); }}
                  className="p-1 hover:bg-gray-100 rounded transition-colors shrink-0 opacity-0 group-hover:opacity-100"
                  aria-label="Delete case"
                >
                  <Trash2 className="h-3.5 w-3.5 text-red-400" />
                </button>
              )}
            </div>
          )}
          <CaseStatusBadge status={caseData.status} />
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {caseData.notes && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {truncateNotes(caseData.notes)}
          </p>
        )}
        <div className="flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" />
            <span>{caseData.owner_name || caseData.owner}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <FileText className="h-3.5 w-3.5" />
            <span>{caseData.document_count} document{caseData.document_count !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <Calendar className="h-3.5 w-3.5" />
            <span>{formatDate(caseData.created_at)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
